/**
 * BrainVisualizer - Neural network visualization
 * Shows the creature's brain structure and activations
 */

import { useSimulationStore } from '../stores/simulationStore';
import { LoadingSpinner, CollapsiblePanel } from './ui';

export function BrainVisualizer() {
  const brain = useSimulationStore((state) => state.selectedCreatureBrain);
  const selectedCreatureId = useSimulationStore((state) => state.selectedCreatureId);

  if (!selectedCreatureId) {
    return (
      <CollapsiblePanel id="brain" title="Red Neuronal" defaultCollapsed>
        <div className="neural-network-placeholder">
          <p>Ninguna criatura seleccionada</p>
        </div>
      </CollapsiblePanel>
    );
  }

  if (!brain) {
    return (
      <CollapsiblePanel id="brain" title="Red Neuronal" defaultCollapsed>
        <LoadingSpinner size="small" text="Cargando cerebro..." />
      </CollapsiblePanel>
    );
  }

  // Validate brain data structure
  if (!brain.nodes || !brain.connections || !Array.isArray(brain.nodes) || !Array.isArray(brain.connections)) {
    return (
      <CollapsiblePanel id="brain" title="Red Neuronal" defaultCollapsed>
        <div className="neural-network-placeholder">
          <p>Datos de cerebro invalidos</p>
        </div>
      </CollapsiblePanel>
    );
  }

  // Simple text representation for now
  // Full visual implementation would use Canvas/SVG
  return (
    <CollapsiblePanel id="brain" title="Red Neuronal" defaultCollapsed>
      <div
        style={{
          fontSize: '0.75rem',
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-secondary)',
        }}
      >
        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Nodos: </span>
          <span style={{ color: 'var(--accent-secondary)' }}>{brain.nodes.length}</span>
        </div>
        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Conexiones: </span>
          <span style={{ color: 'var(--accent-secondary)' }}>{brain.connections.length}</span>
        </div>

        <div
          style={{
            marginTop: 'var(--spacing-md)',
            padding: 'var(--spacing-sm)',
            background: 'var(--bg-panel)',
            borderRadius: 'var(--border-radius)',
            maxHeight: '200px',
            overflow: 'auto',
          }}
        >
          <div style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-xs)' }}>
            Entradas ({brain.nodes.filter((n) => n.type === 'input').length}):
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              marginBottom: 'var(--spacing-sm)',
            }}
          >
            {brain.nodes
              .filter((n) => n.type === 'input')
              .map((node) => (
                <span
                  key={node.id}
                  style={{
                    padding: '2px 4px',
                    background: 'rgba(78, 204, 163, 0.6)',
                    borderRadius: '2px',
                    fontSize: '0.625rem',
                  }}
                  title={`Node ${node.id} (${node.activation})`}
                >
                  {node.id}
                </span>
              ))}
          </div>

          <div style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-xs)' }}>
            Ocultas ({brain.nodes.filter((n) => n.type === 'hidden').length}):
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              marginBottom: 'var(--spacing-sm)',
            }}
          >
            {brain.nodes
              .filter((n) => n.type === 'hidden')
              .map((node) => (
                <span
                  key={node.id}
                  style={{
                    padding: '2px 4px',
                    background: 'rgba(8, 217, 214, 0.6)',
                    borderRadius: '2px',
                    fontSize: '0.625rem',
                  }}
                  title={`Node ${node.id} (${node.activation})`}
                >
                  {node.id}
                </span>
              ))}
          </div>

          <div style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-xs)' }}>
            Salidas ({brain.nodes.filter((n) => n.type === 'output').length}):
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {brain.nodes
              .filter((n) => n.type === 'output')
              .map((node) => (
                <span
                  key={node.id}
                  style={{
                    padding: '2px 4px',
                    background: 'rgba(233, 69, 96, 0.6)',
                    borderRadius: '2px',
                    fontSize: '0.625rem',
                  }}
                  title={`Node ${node.id} (${node.activation})`}
                >
                  {node.id}
                </span>
              ))}
          </div>
        </div>
      </div>
    </CollapsiblePanel>
  );
}
